/**
 * One-time "Scheduling, refreshed" announcement shown on the first
 * open of the InterviewManager scheduling wizard.
 *
 * Wraps the generic `AnnouncementCarousel` with the three slides agreed
 * for SCH-52. Mounted from `ScheduleInterview` and `InterviewActivity`
 * — the hook decides whether to actually display it via localStorage.
 *
 * TODO(SCH-52, existing-customer gate): PM wants this to fire ONLY
 * for existing customers, not new signups. The bootstrap user payload
 * doesn't currently expose an org/account-level onboarding date —
 * `currUser?.created_at` is the individual user's join date, so a new
 * hire at an existing customer would be misclassified. Once product
 * decides on the signal (LD segment flag, BE-emitted boolean on the
 * account, or an account-level `created_at` cutoff), gate the render
 * here alongside `shouldShow`.
 */
import {
  AnnouncementCarousel,
  useOneTimeAnnouncement,
  type AnnouncementSlide,
} from '@kula-ai/components';
import {
  IncludeCalendarInviteAsset,
  SaveAndSendInvitesAsset,
  SelfScheduleSlotSelectionAsset,
} from '@kula-ai/assets';

const STORAGE_KEY = 'scheduling-refreshed-v1';

// 5s per slide is the PM spec for the SCH-52 rollout. Lifted to a
// constant so it's easy to tune without diving into JSX.
const AUTO_SCROLL_MS = 5000;

const SLIDES: AnnouncementSlide[] = [
  {
    media: <SelfScheduleSlotSelectionAsset width="100%" height="100%" />,

    title: 'Interview plans, ready to go',
    description:
      'Zoom host, calendar, private interview, and the self-schedule email template now live in the plan.',
  },
  {
    media: <IncludeCalendarInviteAsset width="100%" height="100%" />,
    title: 'Send an email when scheduling',
    description:
      'You can now send an optional candidate and interviewer email, with an .ics file for every interview',
  },
  {
    media: <SaveAndSendInvitesAsset width="100%" height="100%" />,
    title: 'Smarter rescheduling',
    description:
      'Updated invites only go out when the time actually changes. Swap an interviewer or tweak a scorecard, and the candidate isn’t notified.',
  },
];

export const SchedulingRefreshedAnnouncement = () => {
  const { shouldShow, markSeen } = useOneTimeAnnouncement(STORAGE_KEY);

  if (!shouldShow) return null;

  return (
    <AnnouncementCarousel
      open
      onClose={markSeen}
      headerTitle="Scheduling, refreshed"
      headerSubtitle="Less setup work, better candidate emails, and a reschedule flow that stops spamming calendars"
      slides={SLIDES}
      autoScrollIntervalMs={AUTO_SCROLL_MS}
    />
  );
};
